/**
 * AnimatedDollar — rAF-based animated dollar counter.
 * Uses cubic ease-out (not Framer Motion spring).
 * Displays cents as formatted whole dollars.
 */

import { useState, useEffect, useRef } from 'react';
import { T } from '@/styles/tokens';

interface AnimatedDollarProps {
  value: number;       // cents
  size?: number;       // font size, default 28
  color?: string;      // default T.text
  duration?: number;   // ms, default 600
}

export function AnimatedDollar({ value, size = 28, color = T.text, duration = 600 }: AnimatedDollarProps) {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const start = prevRef.current;
    const end = value;
    const startTime = performance.now();

    const animate = (now: number) => {
      const t = Math.min(1, (now - startTime) / duration);
      const ease = 1 - Math.pow(1 - t, 3); // cubic ease-out
      setDisplay(Math.round(start + (end - start) * ease));
      if (t < 1) rafRef.current = requestAnimationFrame(animate);
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);
    prevRef.current = end;

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration]);

  const dollars = Math.round(display / 100);

  return (
    <span
      style={{
        fontSize: size,
        fontWeight: 800,
        color,
        fontFamily: T.fontFamily,
        letterSpacing: '-0.03em',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1,
      }}
    >
      ${dollars.toLocaleString()}
    </span>
  );
}
